export type Trend = 'up' | 'down' | 'flat'
export type Badge =
  | 'Top Closer'
  | 'Submission King'
  | 'Doc Machine'
  | 'Hot Streak'
  | 'Rising Star'
export type TimePeriod = 'daily' | 'weekly' | 'monthly'

export interface LeaderboardEntry {
  id: string
  name: string
  avatar?: string
  teamName?: string
  docOuts: number
  submissions: number
  ctcs: number
  score: number
  trend: Trend
  badges: Badge[]
  pointDelta?: number
}

// Points system: Doc Out = 10, Submission = 20, CTC = 50
const calcScore = (docOuts: number, subs: number, ctcs: number) =>
  docOuts * 10 + subs * 20 + ctcs * 50

const sortByScore = (entries: LeaderboardEntry[]) =>
  [...entries].sort((a, b) => b.score - a.score)

// ── Individual Data ──────────────────────────────────────────────

const individualsMonthly: LeaderboardEntry[] = sortByScore([
  {
    id: '1',
    name: 'Sarah Jenkins',
    teamName: 'Alpha Squad',
    docOuts: 45,
    submissions: 32,
    ctcs: 18,
    score: calcScore(45, 32, 18),
    trend: 'up',
    badges: ['Top Closer', 'Hot Streak'],
    avatar: 'https://i.pravatar.cc/150?u=1',
    pointDelta: 120,
  },
  {
    id: '2',
    name: 'Michael Chen',
    teamName: 'Beta Force',
    docOuts: 52,
    submissions: 28,
    ctcs: 14,
    score: calcScore(52, 28, 14),
    trend: 'up',
    badges: ['Doc Machine'],
    avatar: 'https://i.pravatar.cc/150?u=2',
    pointDelta: 90,
  },
  {
    id: '3',
    name: 'Jessica Alba',
    teamName: 'Gamma Rays',
    docOuts: 38,
    submissions: 35,
    ctcs: 15,
    score: calcScore(38, 35, 15),
    trend: 'flat',
    badges: ['Submission King'],
    avatar: 'https://i.pravatar.cc/150?u=3',
    pointDelta: 30,
  },
  {
    id: '4',
    name: 'David Smith',
    teamName: 'Alpha Squad',
    docOuts: 41,
    submissions: 25,
    ctcs: 12,
    score: calcScore(41, 25, 12),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=4',
    pointDelta: -40,
  },
  {
    id: '5',
    name: 'Emily Davis',
    teamName: 'Beta Force',
    docOuts: 30,
    submissions: 20,
    ctcs: 10,
    score: calcScore(30, 20, 10),
    trend: 'up',
    badges: ['Rising Star'],
    avatar: 'https://i.pravatar.cc/150?u=5',
    pointDelta: 80,
  },
  {
    id: '6',
    name: 'James Wilson',
    teamName: 'Gamma Rays',
    docOuts: 25,
    submissions: 18,
    ctcs: 8,
    score: calcScore(25, 18, 8),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=6',
    pointDelta: -20,
  },
  {
    id: '7',
    name: 'Ashley Taylor',
    teamName: 'Alpha Squad',
    docOuts: 48,
    submissions: 30,
    ctcs: 16,
    score: calcScore(48, 30, 16),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=7',
    pointDelta: 110,
  },
  {
    id: '8',
    name: 'Robert Brown',
    teamName: 'Beta Force',
    docOuts: 22,
    submissions: 15,
    ctcs: 5,
    score: calcScore(22, 15, 5),
    trend: 'flat',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=8',
    pointDelta: 10,
  },
  {
    id: '9',
    name: 'Linda White',
    teamName: 'Gamma Rays',
    docOuts: 35,
    submissions: 22,
    ctcs: 11,
    score: calcScore(35, 22, 11),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=9',
    pointDelta: 70,
  },
  {
    id: '10',
    name: 'William Harris',
    teamName: 'Alpha Squad',
    docOuts: 28,
    submissions: 19,
    ctcs: 9,
    score: calcScore(28, 19, 9),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=10',
    pointDelta: -30,
  },
])

const individualsWeekly: LeaderboardEntry[] = sortByScore([
  {
    id: '1',
    name: 'Sarah Jenkins',
    teamName: 'Alpha Squad',
    docOuts: 12,
    submissions: 8,
    ctcs: 5,
    score: calcScore(12, 8, 5),
    trend: 'up',
    badges: ['Hot Streak'],
    avatar: 'https://i.pravatar.cc/150?u=1',
    pointDelta: 120,
  },
  {
    id: '2',
    name: 'Michael Chen',
    teamName: 'Beta Force',
    docOuts: 14,
    submissions: 7,
    ctcs: 3,
    score: calcScore(14, 7, 3),
    trend: 'flat',
    badges: ['Doc Machine'],
    avatar: 'https://i.pravatar.cc/150?u=2',
    pointDelta: 30,
  },
  {
    id: '3',
    name: 'Jessica Alba',
    teamName: 'Gamma Rays',
    docOuts: 9,
    submissions: 10,
    ctcs: 4,
    score: calcScore(9, 10, 4),
    trend: 'up',
    badges: ['Submission King'],
    avatar: 'https://i.pravatar.cc/150?u=3',
    pointDelta: 100,
  },
  {
    id: '4',
    name: 'David Smith',
    teamName: 'Alpha Squad',
    docOuts: 10,
    submissions: 6,
    ctcs: 3,
    score: calcScore(10, 6, 3),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=4',
    pointDelta: 90,
  },
  {
    id: '5',
    name: 'Emily Davis',
    teamName: 'Beta Force',
    docOuts: 8,
    submissions: 5,
    ctcs: 3,
    score: calcScore(8, 5, 3),
    trend: 'up',
    badges: ['Rising Star'],
    avatar: 'https://i.pravatar.cc/150?u=5',
    pointDelta: 80,
  },
  {
    id: '6',
    name: 'James Wilson',
    teamName: 'Gamma Rays',
    docOuts: 6,
    submissions: 4,
    ctcs: 2,
    score: calcScore(6, 4, 2),
    trend: 'flat',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=6',
    pointDelta: 0,
  },
  {
    id: '7',
    name: 'Ashley Taylor',
    teamName: 'Alpha Squad',
    docOuts: 11,
    submissions: 9,
    ctcs: 4,
    score: calcScore(11, 9, 4),
    trend: 'up',
    badges: ['Top Closer'],
    avatar: 'https://i.pravatar.cc/150?u=7',
    pointDelta: 130,
  },
  {
    id: '8',
    name: 'Robert Brown',
    teamName: 'Beta Force',
    docOuts: 5,
    submissions: 3,
    ctcs: 1,
    score: calcScore(5, 3, 1),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=8',
    pointDelta: -20,
  },
  {
    id: '9',
    name: 'Linda White',
    teamName: 'Gamma Rays',
    docOuts: 9,
    submissions: 6,
    ctcs: 3,
    score: calcScore(9, 6, 3),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=9',
    pointDelta: -10,
  },
  {
    id: '10',
    name: 'William Harris',
    teamName: 'Alpha Squad',
    docOuts: 7,
    submissions: 5,
    ctcs: 2,
    score: calcScore(7, 5, 2),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=10',
    pointDelta: 50,
  },
])

const individualsDaily: LeaderboardEntry[] = sortByScore([
  {
    id: '1',
    name: 'Sarah Jenkins',
    teamName: 'Alpha Squad',
    docOuts: 3,
    submissions: 2,
    ctcs: 1,
    score: calcScore(3, 2, 1),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=1',
  },
  {
    id: '2',
    name: 'Michael Chen',
    teamName: 'Beta Force',
    docOuts: 4,
    submissions: 1,
    ctcs: 1,
    score: calcScore(4, 1, 1),
    trend: 'up',
    badges: ['Doc Machine'],
    avatar: 'https://i.pravatar.cc/150?u=2',
  },
  {
    id: '3',
    name: 'Jessica Alba',
    teamName: 'Gamma Rays',
    docOuts: 2,
    submissions: 3,
    ctcs: 1,
    score: calcScore(2, 3, 1),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=3',
  },
  {
    id: '4',
    name: 'David Smith',
    teamName: 'Alpha Squad',
    docOuts: 2,
    submissions: 1,
    ctcs: 1,
    score: calcScore(2, 1, 1),
    trend: 'flat',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=4',
  },
  {
    id: '5',
    name: 'Emily Davis',
    teamName: 'Beta Force',
    docOuts: 1,
    submissions: 2,
    ctcs: 0,
    score: calcScore(1, 2, 0),
    trend: 'up',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=5',
  },
  {
    id: '6',
    name: 'James Wilson',
    teamName: 'Gamma Rays',
    docOuts: 2,
    submissions: 1,
    ctcs: 0,
    score: calcScore(2, 1, 0),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=6',
  },
  {
    id: '7',
    name: 'Ashley Taylor',
    teamName: 'Alpha Squad',
    docOuts: 3,
    submissions: 2,
    ctcs: 2,
    score: calcScore(3, 2, 2),
    trend: 'up',
    badges: ['Hot Streak'],
    avatar: 'https://i.pravatar.cc/150?u=7',
  },
  {
    id: '8',
    name: 'Robert Brown',
    teamName: 'Beta Force',
    docOuts: 1,
    submissions: 0,
    ctcs: 0,
    score: calcScore(1, 0, 0),
    trend: 'down',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=8',
  },
  {
    id: '9',
    name: 'Linda White',
    teamName: 'Gamma Rays',
    docOuts: 2,
    submissions: 1,
    ctcs: 1,
    score: calcScore(2, 1, 1),
    trend: 'flat',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=9',
  },
  {
    id: '10',
    name: 'William Harris',
    teamName: 'Alpha Squad',
    docOuts: 1,
    submissions: 1,
    ctcs: 0,
    score: calcScore(1, 1, 0),
    trend: 'flat',
    badges: [],
    avatar: 'https://i.pravatar.cc/150?u=10',
  },
])

// ── Team Data ────────────────────────────────────────────────────

const teamsMonthly: LeaderboardEntry[] = sortByScore([
  {
    id: 't1',
    name: 'Alpha Squad',
    docOuts: 162,
    submissions: 106,
    ctcs: 55,
    score: calcScore(162, 106, 55),
    trend: 'up',
    badges: ['Top Closer'],
    pointDelta: 260,
  },
  {
    id: 't2',
    name: 'Beta Force',
    docOuts: 104,
    submissions: 63,
    ctcs: 29,
    score: calcScore(104, 63, 29),
    trend: 'flat',
    badges: ['Doc Machine'],
    pointDelta: 80,
  },
  {
    id: 't3',
    name: 'Gamma Rays',
    docOuts: 98,
    submissions: 75,
    ctcs: 34,
    score: calcScore(98, 75, 34),
    trend: 'up',
    badges: ['Submission King'],
    pointDelta: 150,
  },
])

const teamsWeekly: LeaderboardEntry[] = sortByScore([
  {
    id: 't1',
    name: 'Alpha Squad',
    docOuts: 40,
    submissions: 28,
    ctcs: 14,
    score: calcScore(40, 28, 14),
    trend: 'up',
    badges: ['Top Closer'],
    pointDelta: 260,
  },
  {
    id: 't2',
    name: 'Beta Force',
    docOuts: 27,
    submissions: 15,
    ctcs: 7,
    score: calcScore(27, 15, 7),
    trend: 'down',
    badges: [],
    pointDelta: -40,
  },
  {
    id: 't3',
    name: 'Gamma Rays',
    docOuts: 24,
    submissions: 20,
    ctcs: 9,
    score: calcScore(24, 20, 9),
    trend: 'up',
    badges: ['Submission King'],
    pointDelta: 120,
  },
])

const teamsDaily: LeaderboardEntry[] = sortByScore([
  {
    id: 't1',
    name: 'Alpha Squad',
    docOuts: 9,
    submissions: 6,
    ctcs: 4,
    score: calcScore(9, 6, 4),
    trend: 'up',
    badges: [],
  },
  {
    id: 't2',
    name: 'Beta Force',
    docOuts: 6,
    submissions: 3,
    ctcs: 1,
    score: calcScore(6, 3, 1),
    trend: 'flat',
    badges: [],
  },
  {
    id: 't3',
    name: 'Gamma Rays',
    docOuts: 6,
    submissions: 5,
    ctcs: 2,
    score: calcScore(6, 5, 2),
    trend: 'up',
    badges: [],
  },
])

// ── Exports ──────────────────────────────────────────────────────

export const individualsByPeriod: Record<TimePeriod, LeaderboardEntry[]> = {
  daily: individualsDaily,
  weekly: individualsWeekly,
  monthly: individualsMonthly,
}

export const teamsByPeriod: Record<TimePeriod, LeaderboardEntry[]> = {
  daily: teamsDaily,
  weekly: teamsWeekly,
  monthly: teamsMonthly,
}

// Keep legacy exports for backward compat
export const individuals = individualsMonthly
export const teams = teamsMonthly
